import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  DataTable,
  Input,
  Sparkline,
  type ColumnDef,
} from '@servicecompressionllc/sc-design-system';
import {
  formatCompact,
  formatCount,
  formatLastActive,
  formatPercent,
  formatUsd,
} from '../../lib/format.js';
import type {
  UsageConversationRow,
  UsageSourceRow,
  UsageSummaryResponse,
  UsageUserRow,
} from '../../lib/types.js';
import { usePagination } from '../../hooks/usePagination.js';
import { sourceName, unitNoun } from './usageLabels.js';

const num = (n: number) => <span className="tabular-nums">{formatCount(n)}</span>;
const money = (n: number | null) => (
  <span className="tabular-nums">{n === null ? 'None' : formatUsd(n)}</span>
);
const share = (n: number | null) => (
  <span className="tabular-nums">{n === null ? 'None' : formatPercent(n)}</span>
);

const conversationColumns: ColumnDef<UsageConversationRow>[] = [
  {
    accessorKey: 'title',
    header: 'Conversation',
    cell: (i) => (
      <span className="font-medium text-ink">
        {i.getValue<string | null>() ?? 'Untitled conversation'}
      </span>
    ),
  },
  { accessorKey: 'userLabel', header: 'User' },
  {
    accessorKey: 'turns',
    header: 'Turns',
    meta: { align: 'right' },
    cell: (i) => num(i.getValue<number>()),
  },
  {
    accessorKey: 'costUsd',
    header: 'Spend',
    meta: { align: 'right' },
    cell: (i) => <span className="tabular-nums text-ink">{formatUsd(i.getValue<number>())}</span>,
  },
  {
    accessorKey: 'costPerTurnUsd',
    header: 'Per turn',
    meta: { align: 'right' },
    cell: (i) => money(i.getValue<number | null>()),
  },
  {
    accessorKey: 'fromCacheShare',
    header: 'From cache',
    meta: { align: 'right' },
    cell: (i) => share(i.getValue<number | null>()),
  },
  {
    accessorKey: 'lastActiveAt',
    header: 'Last active',
    cell: (i) => formatLastActive(i.getValue<string>()),
  },
  {
    id: 'open',
    header: () => <span className="sr-only">Open</span>,
    meta: { align: 'right' },
    cell: ({ row }) => (
      <Link
        to={`/usage/conversations/${encodeURIComponent(row.original.conversationId)}`}
        className="text-link hover:text-link-hover hover:underline"
        aria-label={`Open ${row.original.title ?? 'untitled conversation'}`}
      >
        Open
      </Link>
    ),
  },
];

export const ExpensiveConversationsCard = ({
  rows,
  heading,
  error,
}: {
  rows: UsageConversationRow[] | undefined;
  heading: string;
  error: Error | null;
}) => (
  <Card>
    <div className="flex flex-wrap items-baseline justify-between gap-2 p-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-[1.0625rem] font-semibold text-ink">Most expensive conversations</h3>
        <FromCacheNote />
      </div>
      <span className="text-xs text-ink-subtle">{heading}</span>
    </div>
    <div className="relative overflow-x-auto">
      <DataTable
        columns={conversationColumns}
        data={rows ?? []}
        className="min-w-[53.75rem]"
        emptyMessage={
          error
            ? 'Could not load conversations.'
            : rows
              ? 'No conversations in this period.'
              : 'Loading…'
        }
      />
    </div>
  </Card>
);

const userColumns: ColumnDef<UsageUserRow>[] = [
  {
    accessorKey: 'label',
    header: 'User',
    cell: (i) => <span className="font-medium text-ink">{i.getValue<string>()}</span>,
  },
  {
    accessorKey: 'turns',
    header: 'Turns',
    meta: { align: 'right' },
    cell: (i) => num(i.getValue<number>()),
  },
  {
    accessorKey: 'estimatedCostUsd',
    header: 'Spend',
    meta: { align: 'right' },
    cell: (i) => <span className="tabular-nums text-ink">{formatUsd(i.getValue<number>())}</span>,
  },
  {
    accessorKey: 'costPerTurnUsd',
    header: 'Chat per turn',
    meta: { align: 'right' },
    cell: (i) => money(i.getValue<number | null>()),
  },
  {
    accessorKey: 'otherCostUsd',
    header: 'Letters and other',
    meta: { align: 'right' },
    cell: (i) => money(i.getValue<number | undefined>() ?? null),
  },
  {
    accessorKey: 'fromCacheShare',
    header: 'From cache',
    meta: { align: 'right' },
    cell: (i) => share(i.getValue<number | null>()),
  },
  {
    accessorKey: 'last7DaysUsd',
    header: 'Last 7 days',
    cell: ({ row }) => (
      <Sparkline
        values={row.original.last7DaysUsd}
        ariaLabel={`${row.original.label}, spend over the last 7 days`}
      />
    ),
  },
];

/** Says what the From cache column measures, so nobody has to ask. */
const FromCacheNote = () => (
  <p className="text-xs text-ink-subtle">
    From cache: share of chat input re-read cheaply from the cache. Higher is better.
  </p>
);

const sourceColumns: ColumnDef<UsageSourceRow>[] = [
  {
    accessorKey: 'source',
    header: 'Source',
    cell: (i) => <span className="font-medium text-ink">{sourceName(i.getValue<string>())}</span>,
  },
  {
    accessorKey: 'calls',
    header: 'Count',
    meta: { align: 'right' },
    cell: ({ row }) => (
      <span className="tabular-nums">
        {formatCount(row.original.calls)} {unitNoun(row.original.unit, row.original.calls)}
      </span>
    ),
  },
  {
    accessorKey: 'costUsd',
    header: 'Spend',
    meta: { align: 'right' },
    cell: (i) => <span className="tabular-nums text-ink">{formatUsd(i.getValue<number>())}</span>,
  },
  {
    id: 'each',
    header: 'Each',
    meta: { align: 'right' },
    cell: ({ row }) =>
      money(
        row.original.costPerUnitUsd ??
          (row.original.calls > 0 ? row.original.costUsd / row.original.calls : null),
      ),
  },
  {
    accessorKey: 'fromCacheShare',
    header: 'From cache',
    meta: { align: 'right' },
    cell: (i) => share(i.getValue<number | null | undefined>() ?? null),
  },
];

/** Every kind of work on its own row: chat turns, bot letters, watch deliveries, workflow steps. */
export const SourcesCard = ({ sources }: { sources: UsageSourceRow[] }) => (
  <Card>
    <div className="flex flex-col gap-1 p-5">
      <h3 className="text-[1.0625rem] font-semibold text-ink">Spend by kind of work</h3>
      <p className="text-xs text-ink-subtle">
        Each: spend per chat turn, per letter, per workflow step. From cache: share of its input
        re-read cheaply from the cache.
      </p>
    </div>
    <div className="relative overflow-x-auto">
      <DataTable
        columns={sourceColumns}
        data={sources}
        className="min-w-[40rem]"
        emptyMessage="No spend in this period."
      />
    </div>
  </Card>
);

const USERS_PER_PAGE = 8;
const TOOLS_PER_PAGE = 10;

/** "Showing 8 of 45 users" with Previous and Next, under a paged table. */
const TableFooter = ({
  shown,
  total,
  noun,
  page,
  pageCount,
  onPageChange,
}: {
  shown: number;
  total: number;
  noun: [one: string, many: string];
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}) => (
  <div className="flex items-center justify-between gap-2 border-t border-outline px-4 py-3 text-sm text-ink-muted">
    <span>
      Showing {shown} of {total} {total === 1 ? noun[0] : noun[1]}
    </span>
    <div className="flex gap-1">
      <Button
        size="sm"
        variant="secondary"
        disabled={page === 0}
        onClick={() => onPageChange(page - 1)}
      >
        Previous
      </Button>
      <Button
        size="sm"
        variant="secondary"
        disabled={page >= pageCount - 1}
        onClick={() => onPageChange(page + 1)}
      >
        Next
      </Button>
    </div>
  </div>
);

export const UsersCard = ({ users }: { users: UsageUserRow[] }) => {
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const matching = needle ? users.filter((u) => u.label.toLowerCase().includes(needle)) : users;
  const pager = usePagination(matching, USERS_PER_PAGE);
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 p-5">
        <div className="flex flex-col gap-1">
          <h3 className="text-[1.0625rem] font-semibold text-ink">By user</h3>
          <FromCacheNote />
        </div>
        <label className="flex items-center gap-2">
          <span className="sr-only">Find a user</span>
          <Input
            type="search"
            placeholder="Find a user"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              pager.setPage(0);
            }}
            className="w-[13.75rem]"
          />
        </label>
      </div>
      <div className="relative overflow-x-auto">
        <DataTable
          columns={userColumns}
          data={pager.pageItems}
          className="min-w-[53.75rem]"
          emptyMessage={needle ? 'No user matches that.' : 'No activity in this period.'}
        />
      </div>
      <TableFooter
        shown={pager.pageItems.length}
        total={matching.length}
        noun={['user', 'users']}
        page={pager.page}
        pageCount={pager.pageCount}
        onPageChange={pager.setPage}
      />
    </Card>
  );
};

type ToolRow = UsageSummaryResponse['byTool'][number];

const toolColumns: ColumnDef<ToolRow>[] = [
  {
    accessorKey: 'toolName',
    header: 'Tool',
    cell: (i) => (
      <span className="font-mono text-[0.8125rem] text-ink">{i.getValue<string>()}</span>
    ),
  },
  {
    accessorKey: 'count',
    header: 'Calls',
    meta: { align: 'right' },
    cell: (i) => num(i.getValue<number>()),
  },
  {
    accessorKey: 'avgResultTokens',
    header: 'Avg result size',
    meta: { align: 'right' },
    cell: (i) => {
      const tokens = i.getValue<number | null>();
      return (
        <span className="tabular-nums">
          {tokens === null ? 'Not recorded' : `${formatCompact(tokens)} tokens`}
        </span>
      );
    },
  },
];

export const ToolsCard = ({ tools }: { tools: ToolRow[] }) => {
  const pager = usePagination(tools, TOOLS_PER_PAGE);
  return (
    <Card>
      <div className="p-5">
        <h3 className="text-[1.0625rem] font-semibold text-ink">Top tools</h3>
      </div>
      <div className="relative overflow-x-auto">
        <DataTable
          columns={toolColumns}
          data={pager.pageItems}
          emptyMessage="No tool calls in this period."
        />
      </div>
      <TableFooter
        shown={pager.pageItems.length}
        total={tools.length}
        noun={['tool', 'tools']}
        page={pager.page}
        pageCount={pager.pageCount}
        onPageChange={pager.setPage}
      />
    </Card>
  );
};
